import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, PlaySquare, X } from 'lucide-react';
import { useTheme } from '../../theme';

type ClassLevel = 4 | 5 | 6 | 7 | 8 | 9 | 10;

interface VideoSolutionItem {
  id: string;
  classLevel: ClassLevel;
  question: string;
  url: string;
}

interface VideoSolutionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CLASSES: ClassLevel[] = [4, 5, 6, 7, 8, 9, 10];

const MOCK_VIDEOS: Record<string, VideoSolutionItem[]> = {
  aitutor: [
    {
      id: 'default-4-1',
      classLevel: 4,
      question: 'Find the perimeter of a rectangle (basic example)',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    },
    {
      id: 'default-6-1',
      classLevel: 6,
      question: 'Fractions: add and subtract unlike denominators',
      url: 'https://www.khanacademy.org/math/arithmetic/fraction-arithmetic',
    },
    {
      id: 'default-10-1',
      classLevel: 10,
      question: 'Quadratic equations: factorization method',
      url: 'https://www.youtube.com/watch?v=ZC5Z9R6x7aA',
    },
  ],
  tripura: [
    {
      id: 'tripura-5-1',
      classLevel: 5,
      question: 'Tripura: LCM and HCF (quick tricks)',
      url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
    },
    {
      id: 'tripura-8-1',
      classLevel: 8,
      question: 'Tripura: Simple interest basics',
      url: 'https://www.youtube.com/watch?v=V-_O7nl0Ii0',
    },
  ],
  dps: [
    {
      id: 'dps-4-1',
      classLevel: 4,
      question: 'DPS: Multiplication tables and patterns',
      url: 'https://www.youtube.com/watch?v=3GwjfUFyY6M',
    },
    {
      id: 'dps-9-1',
      classLevel: 9,
      question: 'DPS: Linear equations in two variables',
      url: 'https://www.khanacademy.org/math/algebra/x2f8bb11595b61c86:systems-of-equations',
    },
  ],
};

function toTenantKey(value: string): string {
  const key = value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
  return key || 'AiTutor';
}

export const VideoSolutionsModal: React.FC<VideoSolutionsModalProps> = ({ isOpen, onClose }) => {
  const { branding } = useTheme();
  const tenantKey = useMemo(() => toTenantKey(branding?.tenantName || ''), [branding?.tenantName]);
  const [classLevel, setClassLevel] = useState<ClassLevel>(4);
  const ANIMATION_MS = 320;
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  const items = useMemo(() => {
    const list = MOCK_VIDEOS[tenantKey] ?? MOCK_VIDEOS.default ?? [];
    return list.filter((x) => x.classLevel === classLevel);
  }, [tenantKey, classLevel]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
      return;
    }

    setOpen(false);
    const t = window.setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => window.clearTimeout(t);
  }, [isOpen, ANIMATION_MS]);

  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      <aside
        className={`absolute right-0 top-0 h-full w-full sm:w-[420px] md:w-[520px] lg:w-[640px] bg-white shadow-2xl border-l border-gray-100 flex flex-col transform transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <PlaySquare className="w-5 h-5 text-blue-600" />
            <div>
              <div className="text-sm font-semibold text-gray-900">Video Solutions</div>
              <div className="text-[11px] text-gray-500">Tenant: {branding?.tenantName || 'Default'}</div>
            </div>
          </div>

          <button
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4 min-h-0 flex-1">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Class</div>
            <div className="flex flex-wrap items-center gap-2">
              {CLASSES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setClassLevel(c)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                    classLevel === c
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Class {c}
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 flex flex-col">
            <div className="text-xs font-semibold text-gray-900">Available videos</div>
            <div className="mt-2 flex-1 min-h-0 overflow-y-auto border border-gray-100 rounded-lg">
              {items.length === 0 ? (
                <div className="p-3 text-sm text-gray-600">No videos available for Class {classLevel}.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <div key={item.id} className="p-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900">{item.question}</div>
                        <div className="text-[11px] text-gray-500 break-all">{item.url}</div>
                      </div>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 text-[11px] text-gray-500">Links open in a new tab.</div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default VideoSolutionsModal;
